import { useCallback, useState } from "react"
import { useClient } from "urql"

import { GetPalletByReferenceIdDocument } from "@/graphql/codegen/graphql"
import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGetPalletByReferenceIdReturn {
  fetchPallet: (referenceId: number) => Promise<GoodsReceiptPallet | null>
  isFetching: boolean
}

export const useGetPalletByReferenceId = (): UseGetPalletByReferenceIdReturn => {
  const client = useClient()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isFetching, setIsFetching] = useState(false)

  const fetchPallet = useCallback(
    async (referenceId: number): Promise<GoodsReceiptPallet | null> => {
      setIsFetching(true)

      try {
        const result = await client
          .query(GetPalletByReferenceIdDocument, { referenceId }, { requestPolicy: "network-only" })
          .toPromise()

        if (result.error) {
          notify({
            type: NotificationType.Error,
            silent: false,
            message: "Failed to load pallet. Please try again.",
          })
          return null
        }

        if (result.data?.goodsReceipt.pallet.__typename === "GoodsReceiptPalletGetFailure") {
          notify({
            type: NotificationType.Error,
            silent: false,
            message: result.data.goodsReceipt.pallet.error.message,
          })
          return null
        }

        if (result.data?.goodsReceipt.pallet.__typename === "GoodsReceiptPalletGetSuccess") {
          return result.data.goodsReceipt.pallet.goodsReceiptPallet as GoodsReceiptPallet
        }

        return null
      } catch (error) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: `Failed to load pallet. ${(error as Error).message}`,
        })
        return null
      } finally {
        setIsFetching(false)
      }
    },
    [client, notify]
  )

  return {
    fetchPallet,
    isFetching,
  }
}
